import { useState, useRef, useCallback, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Plus, Search, MoreVertical, Edit, Trash2, Copy, Check, X, Tag, Percent, Calendar, Users, Zap, Building2, Shield, DollarSign, Clock, ToggleLeft, ToggleRight, Ticket, Gift, Image, Bold, Italic, Underline, List, ListOrdered, Link2, AlignLeft, AlignCenter, AlignRight, Type, Globe, Languages, Loader2 } from "lucide-react";
import { tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HelpIcon } from "@/components/HelpIcon";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Multi-language plan names and descriptions
const PLAN_TRANSLATIONS = {
  plan_dev: {
    en: { name: "Developer", description: "For individual developers and small projects" },
    fr: { name: "Développeur", description: "Pour les développeurs individuels et petits projets" },
    de: { name: "Entwickler", description: "Für einzelne Entwickler und kleine Projekte" },
    es: { name: "Desarrollador", description: "Para desarrolladores individuales y proyectos pequeños" },
  },
  plan_biz: {
    en: { name: "Business", description: "For growing teams and businesses" },
    fr: { name: "Entreprise", description: "Pour les équipes et entreprises en croissance" },
    de: { name: "Geschäft", description: "Für wachsende Teams und Unternehmen" },
    es: { name: "Negocio", description: "Para equipos y empresas en crecimiento" },
  },
  plan_ent: {
    en: { name: "Enterprise", description: "For large organizations with custom needs" },
    fr: { name: "Entreprise Plus", description: "Pour les grandes organisations avec des besoins personnalisés" },
    de: { name: "Unternehmen", description: "Für große Organisationen mit individuellen Anforderungen" },
    es: { name: "Empresa", description: "Para grandes organizaciones con necesidades personalizadas" },
  },
  plan_trial: {
    en: { name: "Free Trial", description: "Try our platform risk-free" },
    fr: { name: "Essai Gratuit", description: "Essayez notre plateforme sans risque" },
    de: { name: "Kostenlose Testversion", description: "Testen Sie unsere Plattform risikofrei" },
    es: { name: "Prueba Gratuita", description: "Pruebe nuestra plataforma sin riesgo" },
  },
};

const EXISTING_PLANS = [
  {
    id: "plan_dev",
    name: "Developer",
    slug: "developer",
    price: 49,
    billingPeriod: "monthly",
    status: "active",
    isDefault: false,
    features: ["10K API calls/month", "1 GB bandwidth", "Email support", "Basic analytics"],
    limits: { apiCalls: 10000, bandwidth: 1, vrfRequests: 1000 },
    subscribers: 234,
    createdAt: "2025-01-15",
    expiresAt: null,
    richDescription: "<p>Perfect for <strong>individual developers</strong> building proof-of-concept applications.</p><ul><li>Full API access</li><li>Email-based support</li><li>Standard SLA</li></ul>",
    featuredImage: null,
    translations: PLAN_TRANSLATIONS.plan_dev,
  },
  {
    id: "plan_biz",
    name: "Business",
    slug: "business",
    price: 499,
    billingPeriod: "monthly",
    status: "active",
    isDefault: true,
    features: ["1M API calls/month", "50 GB bandwidth", "Priority support", "Advanced analytics", "Custom webhooks"],
    limits: { apiCalls: 1000000, bandwidth: 50, vrfRequests: 500000 },
    subscribers: 89,
    createdAt: "2025-01-15",
    expiresAt: null,
    richDescription: "<p>Ideal for <strong>growing teams</strong> who need reliable quantum randomness at scale.</p><ul><li>Priority support with 4-hour SLA</li><li>Advanced analytics dashboard</li><li>Webhook integrations</li></ul>",
    featuredImage: null,
    translations: PLAN_TRANSLATIONS.plan_biz,
  },
  {
    id: "plan_ent",
    name: "Enterprise",
    slug: "enterprise",
    price: null,
    billingPeriod: "custom",
    status: "active",
    isDefault: false,
    features: ["Unlimited API calls", "Unlimited bandwidth", "24/7 dedicated support", "SLA guarantee", "Custom integrations", "On-premise option"],
    limits: { apiCalls: -1, bandwidth: -1, vrfRequests: -1 },
    subscribers: 12,
    createdAt: "2025-01-15",
    expiresAt: null,
    richDescription: "<p>Enterprise-grade solution for <strong>mission-critical applications</strong>.</p><ul><li>Dedicated account manager</li><li>Custom SLA agreements</li><li>On-premise deployment options</li><li>SOC 2 compliance reports</li></ul>",
    featuredImage: null,
    translations: PLAN_TRANSLATIONS.plan_ent,
  },
  {
    id: "plan_trial",
    name: "Free Trial",
    slug: "free-trial",
    price: 0,
    billingPeriod: "monthly",
    status: "active",
    isDefault: false,
    features: ["1K API calls/month", "100 MB bandwidth", "Community support"],
    limits: { apiCalls: 1000, bandwidth: 0.1, vrfRequests: 100 },
    subscribers: 567,
    createdAt: "2025-02-01",
    expiresAt: "2025-12-31",
    richDescription: "<p>Get started with quantum randomness <strong>completely free</strong>.</p><p>No credit card required. Upgrade anytime.</p>",
    featuredImage: null,
    translations: PLAN_TRANSLATIONS.plan_trial,
  },
];

const VOUCHERS = [
  { id: "v1", code: "WELCOME20", discountType: "percentage", discountValue: 20, validFrom: "2025-01-01", validUntil: "2025-12-31", usageLimit: 1000, usedCount: 234, status: "active", applicablePlans: ["developer", "business"] },
  { id: "v2", code: "STARTUP50", discountType: "percentage", discountValue: 50, validFrom: "2025-03-01", validUntil: "2025-06-30", usageLimit: 100, usedCount: 45, status: "active", applicablePlans: ["business"] },
  { id: "v3", code: "FLAT100", discountType: "fixed", discountValue: 100, validFrom: "2025-01-01", validUntil: "2025-03-31", usageLimit: 50, usedCount: 50, status: "expired", applicablePlans: ["business", "enterprise"] },
  { id: "v4", code: "ENTERPRISE25", discountType: "percentage", discountValue: 25, validFrom: "2025-06-01", validUntil: "2026-06-01", usageLimit: null, usedCount: 8, status: "active", applicablePlans: ["enterprise"] },
];

const PLAN_FEATURES_TEMPLATE = [
  "API calls/month",
  "Bandwidth",
  "VRF Requests",
  "Email support",
  "Priority support",
  "24/7 dedicated support",
  "Basic analytics",
  "Advanced analytics",
  "Custom webhooks",
  "SLA guarantee",
  "Custom integrations",
  "On-premise option",
];

export default function PlanManagement() {
  const { t, currentLanguage, supportedLanguages } = useLanguage();
  const [activeTab, setActiveTab] = useState("plans");
  const [searchQuery, setSearchQuery] = useState("");
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [editingLanguage, setEditingLanguage] = useState("en");
  const editorRef = useRef(null);

  // Real plans from backend
  const [livePlans, setLivePlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/plans/`, {
          headers: { Authorization: `Bearer ${tokenStore.getAccess()}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Map backend plan to EXISTING_PLANS shape
          setLivePlans(data.map(p => ({
            id: p.id,
            name: p.name,
            slug: p.slug,
            price: p.slug === "enterprise" ? null : p.price_monthly,
            billingPeriod: "monthly",
            status: p.is_active ? "active" : "inactive",
            isDefault: p.slug === "business",
            features: p.features || [],
            limits: {
              apiCalls: p.limits?.api_calls_per_month ?? 0,
              bandwidth: p.limits?.bandwidth_gb ?? 0,
              vrfRequests: p.limits?.api_calls_per_month ?? 0,
            },
            subscribers: 0,
            createdAt: p.created_at ? p.created_at.split("T")[0] : "",
            expiresAt: null,
            richDescription: p.description || "",
            featuredImage: null,
            translations: {
              en: { name: p.name, description: p.description || "" },
              fr: { name: "", description: "" },
              de: { name: "", description: "" },
              es: { name: "", description: "" },
            },
          })));
        }
      } catch { /* fall back to static data */ }
      finally { setPlansLoading(false); }
    })();
  }, []);

  // Plan form state
  const [planName, setPlanName] = useState("");
  const [planSlug, setPlanSlug] = useState("");
  const [planPrice, setPlanPrice] = useState("");
  const [planBillingPeriod, setPlanBillingPeriod] = useState("monthly");
  const [planFeatures, setPlanFeatures] = useState([]);
  const [planApiCalls, setPlanApiCalls] = useState("");
  const [planBandwidth, setPlanBandwidth] = useState("");
  const [planVrfRequests, setPlanVrfRequests] = useState("");
  const [planExpiresAt, setPlanExpiresAt] = useState("");
  const [planIsDefault, setPlanIsDefault] = useState(false);
  const [planStatus, setPlanStatus] = useState("active");
  const [planRichDescription, setPlanRichDescription] = useState("");
  const [planFeaturedImage, setPlanFeaturedImage] = useState("");
  const [planTranslations, setPlanTranslations] = useState({
    en: { name: "", description: "" },
    fr: { name: "", description: "" },
    de: { name: "", description: "" },
    es: { name: "", description: "" },
  });

  // Voucher form state
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherDiscountType, setVoucherDiscountType] = useState("percentage");
  const [voucherDiscountValue, setVoucherDiscountValue] = useState("");
  const [voucherValidFrom, setVoucherValidFrom] = useState("");
  const [voucherValidUntil, setVoucherValidUntil] = useState("");
  const [voucherUsageLimit, setVoucherUsageLimit] = useState("");
  const [voucherApplicablePlans, setVoucherApplicablePlans] = useState([]);

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setPlanName("");
    setPlanSlug("");
    setPlanPrice("");
    setPlanBillingPeriod("monthly");
    setPlanFeatures([]);
    setPlanApiCalls("");
    setPlanBandwidth("");
    setPlanVrfRequests("");
    setPlanExpiresAt("");
    setPlanIsDefault(false);
    setPlanStatus("active");
    setPlanRichDescription("");
    setPlanFeaturedImage("");
    setPlanTranslations({
      en: { name: "", description: "" },
      fr: { name: "", description: "" },
      de: { name: "", description: "" },
      es: { name: "", description: "" },
    });
    setEditingLanguage("en");
    setPlanDialogOpen(true);
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setPlanName(plan.name);
    setPlanSlug(plan.slug);
    setPlanPrice(plan.price?.toString() || "");
    setPlanBillingPeriod(plan.billingPeriod);
    setPlanFeatures(plan.features);
    setPlanApiCalls(plan.limits.apiCalls.toString());
    setPlanBandwidth(plan.limits.bandwidth.toString());
    setPlanVrfRequests(plan.limits.vrfRequests.toString());
    setPlanExpiresAt(plan.expiresAt || "");
    setPlanIsDefault(plan.isDefault);
    setPlanStatus(plan.status);
    setPlanRichDescription(plan.richDescription || "");
    setPlanFeaturedImage(plan.featuredImage || "");
    setPlanTranslations(plan.translations || {
      en: { name: plan.name, description: "" },
      fr: { name: "", description: "" },
      de: { name: "", description: "" },
      es: { name: "", description: "" },
    });
    setEditingLanguage("en");
    setPlanDialogOpen(true);
  };

  // WYSIWYG Editor Functions
  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setPlanRichDescription(editorRef.current.innerHTML);
    }
  }, []);

  const handleEditorInput = useCallback(() => {
    if (editorRef.current) {
      setPlanRichDescription(editorRef.current.innerHTML);
    }
  }, []);

  const insertImage = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      execCommand("insertImage", url);
    }
  };

  const insertLink = () => {
    const url = prompt("Enter link URL:");
    if (url) {
      execCommand("createLink", url);
    }
  };

  const updateTranslation = (lang, field, value) => {
    setPlanTranslations(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value }
    }));
  };

  const handleCreateVoucher = () => {
    setSelectedVoucher(null);
    setVoucherCode("");
    setVoucherDiscountType("percentage");
    setVoucherDiscountValue("");
    setVoucherValidFrom("");
    setVoucherValidUntil("");
    setVoucherUsageLimit("");
    setVoucherApplicablePlans([]);
    setVoucherDialogOpen(true);
  };

  const handleEditVoucher = (voucher) => {
    setSelectedVoucher(voucher);
    setVoucherCode(voucher.code);
    setVoucherDiscountType(voucher.discountType);
    setVoucherDiscountValue(voucher.discountValue.toString());
    setVoucherValidFrom(voucher.validFrom);
    setVoucherValidUntil(voucher.validUntil);
    setVoucherUsageLimit(voucher.usageLimit?.toString() || "");
    setVoucherApplicablePlans(voucher.applicablePlans);
    setVoucherDialogOpen(true);
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const togglePlanFeature = (feature) => {
    setPlanFeatures(prev =>
      prev.includes(feature) ? prev.filter(f => f !== feature) : [...prev, feature]
    );
  };

  const toggleVoucherPlan = (planSlug) => {
    setVoucherApplicablePlans(prev =>
      prev.includes(planSlug) ? prev.filter(p => p !== planSlug) : [...prev, planSlug]
    );
  };

  const displayPlans = livePlans.length > 0 ? livePlans : EXISTING_PLANS;
  const filteredPlans = displayPlans.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredVouchers = VOUCHERS.filter(v =>
    v.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="plan-management-page">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="plan-management-title">
            Plan & Pricing Management
          </h1>
          <HelpIcon featureTag="plans" portalType="admin" />
        </div>
        <p className="text-sm text-slate-500 mt-1">Create and manage subscription plans, pricing tiers, and discount vouchers</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Tag className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{displayPlans.length}</div>
              <div className="text-xs text-slate-500">Active Plans</div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{EXISTING_PLANS.reduce((sum, p) => sum + p.subscribers, 0)}</div>
              <div className="text-xs text-slate-500">Total Subscribers</div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <Ticket className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{VOUCHERS.filter(v => v.status === 'active').length}</div>
              <div className="text-xs text-slate-500">Active Vouchers</div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <Gift className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{VOUCHERS.reduce((sum, v) => sum + v.usedCount, 0)}</div>
              <div className="text-xs text-slate-500">Vouchers Redeemed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="plans">Subscription Plans</TabsTrigger>
            <TabsTrigger value="vouchers">Discount Vouchers</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={activeTab === "plans" ? "Search plans..." : "Search vouchers..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            {activeTab === "plans" ? (
              <Button onClick={handleCreatePlan} data-testid="create-plan-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Plan
              </Button>
            ) : (
              <Button onClick={handleCreateVoucher} data-testid="create-voucher-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Voucher
              </Button>
            )}
          </div>
        </div>

        {/* Plans Tab */}
        <TabsContent value="plans" className="m-0">
          {plansLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading plans...
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
            {filteredPlans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white border rounded-lg overflow-hidden ${plan.isDefault ? 'border-cyan-300 ring-1 ring-cyan-100' : 'border-slate-200'}`}
                data-testid={`plan-card-${plan.slug}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        plan.slug === 'developer' ? 'bg-blue-100' :
                        plan.slug === 'business' ? 'bg-purple-100' :
                        plan.slug === 'enterprise' ? 'bg-amber-100' : 'bg-emerald-100'
                      }`}>
                        {plan.slug === 'developer' && <Zap className="w-5 h-5 text-blue-600" />}
                        {plan.slug === 'business' && <Building2 className="w-5 h-5 text-purple-600" />}
                        {plan.slug === 'enterprise' && <Shield className="w-5 h-5 text-amber-600" />}
                        {plan.slug === 'free-trial' && <Gift className="w-5 h-5 text-emerald-600" />}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{plan.name}</h3>
                        <code className="text-[10px] text-slate-400">{plan.slug}</code>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditPlan(plan)}>
                          <Edit className="w-4 h-4 mr-2" /> Edit Plan
                        </DropdownMenuItem>
                        <DropdownMenuItem><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="text-3xl font-bold text-slate-900">
                      {plan.price === null ? 'Custom' : plan.price === 0 ? 'Free' : `$${plan.price}`}
                    </span>
                    {plan.price > 0 && <span className="text-sm text-slate-500">/{plan.billingPeriod === 'monthly' ? 'mo' : plan.billingPeriod}</span>}
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    <Badge variant="outline" className={`text-[10px] ${plan.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {plan.status}
                    </Badge>
                    {plan.isDefault && (
                      <Badge className="bg-cyan-100 text-cyan-700 text-[10px] border-0">Default</Badge>
                    )}
                    {plan.expiresAt && (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                        <Clock className="w-3 h-3 mr-1" /> Expires {plan.expiresAt}
                      </Badge>
                    )}
                  </div>

                  <ul className="space-y-1.5 mb-4">
                    {plan.features.slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-slate-600">
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                    {plan.features.length > 4 && (
                      <li className="text-xs text-slate-400">+{plan.features.length - 4} more features</li>
                    )}
                  </ul>

                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                    <span><Users className="w-3.5 h-3.5 inline mr-1" /> {plan.subscribers} subscribers</span>
                    <span>Created {plan.createdAt}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </TabsContent>

        {/* Vouchers Tab */}
        <TabsContent value="vouchers" className="m-0">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="vouchers-table">
            <div>
              <div className="grid grid-cols-[160px_120px_160px_100px_1fr_100px_44px] bg-slate-50 border-b border-slate-200">
                {["Voucher Code","Discount","Validity","Usage","Applicable Plans",t("label.status"),t("label.actions")].map((h,i) => (
                  <div key={i} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
                ))}
              </div>
              {filteredVouchers.map((voucher) => (
                <div key={voucher.id} className="grid grid-cols-[160px_120px_160px_100px_1fr_100px_44px] border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`voucher-row-${voucher.code}`}>
                  <div className="px-4 py-3 flex items-center gap-2">
                    <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono font-medium text-slate-900">{voucher.code}</code>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"><Copy className="w-3 h-3" /></Button>
                  </div>
                  <div className="px-4 py-3 flex items-center gap-1.5">
                    {voucher.discountType === 'percentage' ? (
                      <><Percent className="w-4 h-4 text-purple-500" /><span className="font-semibold text-slate-900">{voucher.discountValue}%</span></>
                    ) : (
                      <><DollarSign className="w-4 h-4 text-emerald-500" /><span className="font-semibold text-slate-900">${voucher.discountValue}</span></>
                    )}
                  </div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{voucher.validFrom} to {voucher.validUntil}</div>
                  <div className="px-4 py-3 flex items-center">
                    <span className="text-sm"><span className="font-medium text-slate-900">{voucher.usedCount}</span><span className="text-slate-400"> / {voucher.usageLimit || '∞'}</span></span>
                  </div>
                  <div className="px-4 py-3 flex items-center flex-wrap gap-1">
                    {voucher.applicablePlans.map((plan) => (
                      <Badge key={plan} variant="outline" className="text-[10px] capitalize">{plan}</Badge>
                    ))}
                  </div>
                  <div className="px-4 py-3 flex items-center">
                    <Badge variant="outline" className={`text-[10px] ${
                      voucher.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      voucher.status === 'expired' ? 'bg-red-50 text-red-700 border-red-200' :
                      'bg-slate-50 text-slate-600 border-slate-200'
                    }`}>{voucher.status}</Badge>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditVoucher(voucher)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                        <DropdownMenuItem><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                        {voucher.status === 'active' ? (
                          <DropdownMenuItem><ToggleLeft className="w-4 h-4 mr-2" /> Deactivate</DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem><ToggleRight className="w-4 h-4 mr-2" /> Activate</DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Plan Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="plan-dialog-desc">
          <DialogHeader>
            <DialogTitle>{selectedPlan ? t("plan.edit_plan", "Edit Plan") : t("plan.create_plan", "Create New Plan")}</DialogTitle>
            <DialogDescription id="plan-dialog-desc">
              {t("plan.dialog_desc", "Configure plan details, pricing, features, rich content, and localization")}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">{t("plan.basic_info", "Basic Info")}</TabsTrigger>
              <TabsTrigger value="content">{t("plan.rich_content", "Rich Content")}</TabsTrigger>
              <TabsTrigger value="features">{t("plan.features", "Features")}</TabsTrigger>
              <TabsTrigger value="translations"><Languages className="w-4 h-4 mr-1" />{t("plan.translations", "Translations")}</TabsTrigger>
            </TabsList>
            
            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-5 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">{t("plan.plan_name", "Plan Name")} *</Label>
                  <Input
                    value={planName}
                    onChange={(e) => {
                      setPlanName(e.target.value);
                      if (!selectedPlan) setPlanSlug(generateSlug(e.target.value));
                      updateTranslation("en", "name", e.target.value);
                    }}
                    placeholder="e.g., Business Pro"
                    className="mt-1"
                    data-testid="plan-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("plan.slug", "Slug")} *</Label>
                  <Input
                    value={planSlug}
                    onChange={(e) => setPlanSlug(e.target.value)}
                    placeholder="business-pro"
                    className="mt-1 font-mono"
                    data-testid="plan-slug-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">{t("plan.price", "Price (USD)")}</Label>
                  <Input
                    type="number"
                    value={planPrice}
                    onChange={(e) => setPlanPrice(e.target.value)}
                    placeholder={t("plan.price_placeholder", "0 for free, empty for custom")}
                    className="mt-1"
                    data-testid="plan-price-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("plan.billing_period", "Billing Period")}</Label>
                  <Select value={planBillingPeriod} onValueChange={setPlanBillingPeriod}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">{t("plan.monthly", "Monthly")}</SelectItem>
                      <SelectItem value="yearly">{t("plan.yearly", "Yearly")}</SelectItem>
                      <SelectItem value="custom">{t("plan.custom", "Custom")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("label.status")}</Label>
                  <Select value={planStatus} onValueChange={setPlanStatus}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t("label.active", "Active")}</SelectItem>
                      <SelectItem value="inactive">{t("label.inactive", "Inactive")}</SelectItem>
                      <SelectItem value="deprecated">{t("plan.deprecated", "Deprecated")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">{t("plan.expiry_date", "Plan Expiry Date")} ({t("label.optional", "Optional")})</Label>
                  <Input
                    type="date"
                    value={planExpiresAt}
                    onChange={(e) => setPlanExpiresAt(e.target.value)}
                    className="mt-1"
                    data-testid="plan-expiry-input"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">{t("plan.no_expiry_hint", "Leave empty for no expiry")}</p>
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <div className="flex items-center gap-2">
                    <Switch checked={planIsDefault} onCheckedChange={setPlanIsDefault} id="plan-default" />
                    <Label htmlFor="plan-default" className="text-sm">{t("plan.set_default", "Set as default plan")}</Label>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs mb-3 block">{t("plan.usage_limits", "Usage Limits")}</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[10px] text-slate-500">{t("plan.api_calls", "API Calls/month")}</Label>
                    <Input
                      type="number"
                      value={planApiCalls}
                      onChange={(e) => setPlanApiCalls(e.target.value)}
                      placeholder="-1 for unlimited"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">{t("plan.bandwidth", "Bandwidth (GB)")}</Label>
                    <Input
                      type="number"
                      value={planBandwidth}
                      onChange={(e) => setPlanBandwidth(e.target.value)}
                      placeholder="-1 for unlimited"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">{t("plan.vrf_requests", "VRF Requests/month")}</Label>
                    <Input
                      type="number"
                      value={planVrfRequests}
                      onChange={(e) => setPlanVrfRequests(e.target.value)}
                      placeholder="-1 for unlimited"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Rich Content Tab - WYSIWYG Editor */}
            <TabsContent value="content" className="space-y-5 pt-4">
              <div>
                <Label className="text-xs mb-2 block">{t("plan.featured_image", "Featured Image URL")}</Label>
                <div className="flex gap-2">
                  <Input
                    value={planFeaturedImage}
                    onChange={(e) => setPlanFeaturedImage(e.target.value)}
                    placeholder="https://example.com/image.png"
                    className="flex-1"
                    data-testid="plan-featured-image-input"
                  />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="sm"><Image className="w-4 h-4" /></Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("plan.add_image", "Add image for plan card")}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {planFeaturedImage && (
                  <div className="mt-2 p-2 border rounded bg-slate-50">
                    <img src={planFeaturedImage} alt="Preview" className="max-h-24 object-contain" onError={(e) => e.target.style.display='none'} />
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs mb-2 block">{t("plan.rich_description", "Rich Description")} (WYSIWYG)</Label>
                
                {/* WYSIWYG Toolbar */}
                <div className="flex flex-wrap items-center gap-1 p-2 border border-b-0 rounded-t bg-slate-50">
                  <TooltipProvider>
                    <div className="flex items-center gap-1 pr-2 border-r">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => execCommand("bold")}>
                            <Bold className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.bold", "Bold")}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => execCommand("italic")}>
                            <Italic className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.italic", "Italic")}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => execCommand("underline")}>
                            <Underline className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.underline", "Underline")}</TooltipContent>
                      </Tooltip>
                    </div>
                    
                    <div className="flex items-center gap-1 px-2 border-r">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => execCommand("insertUnorderedList")}>
                            <List className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.bullet_list", "Bullet List")}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => execCommand("insertOrderedList")}>
                            <ListOrdered className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.numbered_list", "Numbered List")}</TooltipContent>
                      </Tooltip>
                    </div>
                    
                    <div className="flex items-center gap-1 px-2 border-r">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => execCommand("justifyLeft")}>
                            <AlignLeft className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.align_left", "Align Left")}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => execCommand("justifyCenter")}>
                            <AlignCenter className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.align_center", "Align Center")}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => execCommand("justifyRight")}>
                            <AlignRight className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.align_right", "Align Right")}</TooltipContent>
                      </Tooltip>
                    </div>
                    
                    <div className="flex items-center gap-1 px-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={insertLink}>
                            <Link2 className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.insert_link", "Insert Link")}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={insertImage}>
                            <Image className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("plan.insert_image", "Insert Image")}</TooltipContent>
                      </Tooltip>
                    </div>
                    
                    <div className="flex items-center gap-1 ml-auto">
                      <Select defaultValue="p" onValueChange={(v) => execCommand("formatBlock", v)}>
                        <SelectTrigger className="h-8 w-32">
                          <Type className="w-3 h-3 mr-1" /><SelectValue placeholder="Format" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="p">{t("plan.paragraph", "Paragraph")}</SelectItem>
                          <SelectItem value="h1">{t("plan.heading1", "Heading 1")}</SelectItem>
                          <SelectItem value="h2">{t("plan.heading2", "Heading 2")}</SelectItem>
                          <SelectItem value="h3">{t("plan.heading3", "Heading 3")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TooltipProvider>
                </div>
                
                {/* Editable Content Area */}
                <div
                  ref={editorRef}
                  contentEditable
                  className="min-h-[200px] p-4 border rounded-b bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 prose prose-sm max-w-none"
                  onInput={handleEditorInput}
                  dangerouslySetInnerHTML={{ __html: planRichDescription }}
                  data-testid="plan-wysiwyg-editor"
                />
                <p className="text-[10px] text-slate-400 mt-1">{t("plan.wysiwyg_hint", "Use the toolbar above to format text, add lists, links, and images")}</p>
              </div>
            </TabsContent>

            {/* Features Tab */}
            <TabsContent value="features" className="space-y-5 pt-4">
              <div>
                <Label className="text-xs mb-3 block">{t("plan.plan_features", "Plan Features")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PLAN_FEATURES_TEMPLATE.map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <Checkbox
                        checked={planFeatures.includes(feature)}
                        onCheckedChange={() => togglePlanFeature(feature)}
                        id={`feature-${feature}`}
                      />
                      <label htmlFor={`feature-${feature}`} className="text-sm text-slate-700">{feature}</label>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Translations Tab */}
            <TabsContent value="translations" className="space-y-5 pt-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-xs">{t("plan.select_language", "Select Language to Edit")}</Label>
                  <p className="text-[10px] text-slate-400">{t("plan.i18n_hint", "Provide translations for each supported language")}</p>
                </div>
                <Select value={editingLanguage} onValueChange={setEditingLanguage}>
                  <SelectTrigger className="w-48">
                    <Globe className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {supportedLanguages.slice(0, 4).map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="flex items-center gap-2">
                          <span>{lang.nativeName}</span>
                          {planTranslations[lang.code]?.name && <Check className="w-3 h-3 text-emerald-500" />}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 border rounded-lg bg-slate-50 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Globe className="w-4 h-4" />
                  {t("plan.editing_language", "Editing:")} {supportedLanguages.find(l => l.code === editingLanguage)?.name}
                </div>
                
                <div>
                  <Label className="text-xs">{t("plan.translated_name", "Plan Name")} ({editingLanguage.toUpperCase()})</Label>
                  <Input
                    value={planTranslations[editingLanguage]?.name || ""}
                    onChange={(e) => updateTranslation(editingLanguage, "name", e.target.value)}
                    placeholder={planName || "Plan name in this language"}
                    className="mt-1"
                    data-testid={`plan-name-${editingLanguage}`}
                  />
                </div>
                
                <div>
                  <Label className="text-xs">{t("plan.translated_desc", "Short Description")} ({editingLanguage.toUpperCase()})</Label>
                  <Textarea
                    value={planTranslations[editingLanguage]?.description || ""}
                    onChange={(e) => updateTranslation(editingLanguage, "description", e.target.value)}
                    placeholder="Short description in this language"
                    className="mt-1"
                    rows={3}
                    data-testid={`plan-desc-${editingLanguage}`}
                  />
                </div>
              </div>

              {/* Translation Status */}
              <div className="grid grid-cols-4 gap-2">
                {supportedLanguages.slice(0, 4).map((lang) => {
                  const hasTranslation = planTranslations[lang.code]?.name;
                  return (
                    <div
                      key={lang.code}
                      className={`p-2 rounded border text-center text-xs ${hasTranslation ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
                    >
                      <div className="font-medium">{lang.code.toUpperCase()}</div>
                      <div>{hasTranslation ? t("label.done", "Done") : t("label.pending", "Pending")}</div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>{t("action.cancel")}</Button>
            <Button data-testid="save-plan-btn">{selectedPlan ? t("plan.update_plan", "Update Plan") : t("plan.create_plan", "Create Plan")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Voucher Dialog */}
      <Dialog open={voucherDialogOpen} onOpenChange={setVoucherDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="voucher-dialog-desc">
          <DialogHeader>
            <DialogTitle>{selectedVoucher ? 'Edit Voucher' : 'Create Discount Voucher'}</DialogTitle>
            <DialogDescription id="voucher-dialog-desc">
              Configure voucher code, discount, and validity
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-xs">Voucher Code *</Label>
              <Input
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                placeholder="e.g., SAVE20"
                className="mt-1 font-mono uppercase"
                data-testid="voucher-code-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Discount Type</Label>
                <Select value={voucherDiscountType} onValueChange={setVoucherDiscountType}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Discount Value *</Label>
                <Input
                  type="number"
                  value={voucherDiscountValue}
                  onChange={(e) => setVoucherDiscountValue(e.target.value)}
                  placeholder={voucherDiscountType === 'percentage' ? "e.g., 20" : "e.g., 50"}
                  className="mt-1"
                  data-testid="voucher-value-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Valid From *</Label>
                <Input
                  type="date"
                  value={voucherValidFrom}
                  onChange={(e) => setVoucherValidFrom(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Valid Until *</Label>
                <Input
                  type="date"
                  value={voucherValidUntil}
                  onChange={(e) => setVoucherValidUntil(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Usage Limit</Label>
              <Input
                type="number"
                value={voucherUsageLimit}
                onChange={(e) => setVoucherUsageLimit(e.target.value)}
                placeholder="Leave empty for unlimited"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs mb-2 block">Applicable Plans</Label>
              <div className="space-y-2">
                {displayPlans.filter(p => p.price > 0 || p.price === null).map((plan) => (
                  <div key={plan.slug} className="flex items-center gap-2">
                    <Checkbox
                      checked={voucherApplicablePlans.includes(plan.slug)}
                      onCheckedChange={() => toggleVoucherPlan(plan.slug)}
                      id={`voucher-plan-${plan.slug}`}
                    />
                    <label htmlFor={`voucher-plan-${plan.slug}`} className="text-sm text-slate-700">{plan.name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoucherDialogOpen(false)}>{t("action.cancel")}</Button>
            <Button data-testid="save-voucher-btn">{selectedVoucher ? 'Update Voucher' : 'Create Voucher'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
